import styled from "styled-components";
import {
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  CircularProgress,
  Link,
} from "@mui/material";
import theme from "theme";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as CcfoliaProLogo } from "./CcfoliaProLogo.svg";
import { ReactComponent as SoundMasterLogo } from "containers/PlanSelect/SoundMasterLogo.svg";
import StaticHeader from "containers/StaticHeader";
import SoundMasterJoinConfirmDialog from "containers/SoundMasterJoinConfirmDialog";
import {
  createSubscription,
  joinSubscription,
  joinSoundMaster,
  getSoundMasterPrice,
} from "api";
import { getStripe } from "modules/stripe";
import { auth } from "initializer";
import { useEffect, useState, ReactNode } from "react";
import { getIdTokenResult, onAuthStateChanged } from "firebase/auth";

const Welcome = () => {
  const [t] = useTranslation();
  const year = new Date().getFullYear();
  const [isPro, setIsPro] = useState(false);
  const [isSoundMaster, setIsSoundMaster] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [soundMasterPrice, setSoundMasterPrice] = useState<number | null>(null);
  const [campaignPrice, setCampaignPrice] = useState<number | null>(null);
  const [campaignLabel, setCampaignLabel] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        getIdTokenResult(user).then((token) => {
          setIsPro(token.claims.plan === "ccfolia-pro");
          setIsSoundMaster(Boolean(token.claims.isSoundMaster));
        });
      }
    });

    return unsubscribe;
  }, [setIsPro, setIsSoundMaster]);

  useEffect(() => {
    getSoundMasterPrice().then((data) => {
      console.log(data);

      setSoundMasterPrice(data.price);
      setCampaignPrice(data.campaignPrice ?? null);
      setCampaignLabel(data.campaignLabel ?? null);
    });
  }, []);

  const onClickUpgrade = async () => {
    if (isPro) {
      return;
    }

    if (auth.currentUser == null || auth.currentUser.isAnonymous) {
      window.alert(t("CCFOLIA PROに加入するには、ログインしてください。"));
      return;
    }

    const { url } = await createSubscription();
    window.location.href = url;
  };

  const onClickUpgradeWithSoundMaster = async () => {
    if (isPro && isSoundMaster) {
      return;
    }

    if (auth.currentUser == null || auth.currentUser.isAnonymous) {
      window.alert(
        t("CCFOLIA PRO + SOUND MASTER に加入するには、ログインしてください。")
      );
      return;
    }

    // If already Pro, show confirmation dialog
    if (isPro) {
      setOpenConfirmDialog(true);
    } else {
      // Not Pro yet, join both Pro and Sound Master via Stripe
      const { url } = await joinSubscription(["pro", "sound-master"]);
      window.location.href = url;
    }
  };

  const handleConfirmJoinSoundMaster = async () => {
    setConfirmLoading(true);
    try {
      const res = await joinSoundMaster(soundMasterPrice ?? 880, undefined);

      if (res.status === "failure") {
        window.alert(t("加入に失敗しました"));
        setConfirmLoading(false);
        return;
      }

      if (res.status === "expired_preview") {
        window.alert(t("プレビューの有効期限が切れました"));
        return;
      }

      if (res.status === "active") {
        // Redirect to /home?success to update user state
        window.location.href = "/home?success";
        return;
      }

      const stripe = await getStripe();
      const result = await stripe.confirmCardPayment(res.clientSecret);

      if (result.error) {
        window.alert(t("カードのセキュリティチェックでエラーが発生しました"));
        console.log(result.error);
        setConfirmLoading(false);
        return;
      }

      if (result.paymentIntent.status !== "succeeded") {
        window.alert(t("カードのセキュリティチェックでエラーが発生しました"));
        setConfirmLoading(false);
        return;
      }

      // Redirect to /home?success to update user state
      window.location.href = "/home?success";
    } catch (error) {
      console.error(error);
      window.alert(t("エラーが発生しました"));
      setConfirmLoading(false);
    }
  };

  const handleCloseConfirmDialog = () => {
    if (!confirmLoading) {
      setOpenConfirmDialog(false);
    }
  };

  const effectivePrice = campaignPrice ?? soundMasterPrice ?? 880;
  const totalPrice = 500 + effectivePrice;
  const hasCampaign = campaignPrice !== null;

  return (
    <StyledWrapper>
      <SoundMasterJoinConfirmDialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmJoinSoundMaster}
        loading={confirmLoading}
        soundMasterPrice={soundMasterPrice ?? undefined}
        campaignPrice={campaignPrice ?? undefined}
        campaignLabel={campaignLabel ?? undefined}
      />
      <StaticHeader />
      <FirstSection>
        <StyledTitle
          variant="h4"
          align="center"
          color="textPrimary"
          gutterBottom
        >
          {t("提供プラン")}
        </StyledTitle>
        <Container maxWidth="md">
          <Grid
            container
            spacing={2}
            alignItems="stretch"
            justifyContent="center"
          >
            <Grid item xs={12} sm={4}>
              <Card
                variant="outlined"
                style={{
                  height: "100%",
                  background: theme.palette.grey[900],
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent
                  style={{
                    background: theme.palette.background.default,
                  }}
                >
                  <LogoWrapper>
                    <Typography align="center">CCFOLIA BASIC</Typography>
                  </LogoWrapper>
                  <Typography
                    variant="caption"
                    align="center"
                    display="block"
                    color="textSecondary"
                  >
                    {t("これまで通りの基本プラン")}
                  </Typography>
                </CardContent>
                <PriceArea>
                  <Typography variant="h4" align="center">
                    {t("無料")}
                  </Typography>
                </PriceArea>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card
                variant="outlined"
                style={{
                  borderColor: theme.palette.secondary.main,
                  height: "100%",
                  background: theme.palette.grey[900],
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent
                  style={{
                    background: theme.palette.background.default,
                  }}
                >
                  <LogoWrapper>
                    <CcfoliaProLogo role="img" aria-label="CCFOLIA PRO" />
                  </LogoWrapper>
                  <Typography
                    variant="caption"
                    align="center"
                    display="block"
                    color="textSecondary"
                  >
                    {t("ココフォリアのプロになれる新プラン")}
                  </Typography>
                </CardContent>
                <PriceArea>
                  <Typography variant="h4" align="center">
                    <Typography variant="caption">{t("月々")}</Typography> 500{" "}
                    <Typography variant="caption">{t("円")}</Typography>
                  </Typography>
                </PriceArea>
                <CardActions>
                  <ButtonUpgrade isPro={isPro} onClick={onClickUpgrade} />
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card
                variant="outlined"
                style={{
                  borderColor: theme.palette.secondary.main,
                  height: "100%",
                  background: theme.palette.grey[900],
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent
                  style={{
                    background: theme.palette.background.default,
                  }}
                >
                  <SoundMasterLogoWrapper>
                    <CombinedLogoWrapper>
                      <CcfoliaProLogo role="img" aria-label="CCFOLIA PRO" />
                      <SoundMasterLogo role="img" aria-label="SOUND MASTER" />
                    </CombinedLogoWrapper>
                  </SoundMasterLogoWrapper>
                  <Typography
                    variant="caption"
                    align="center"
                    display="block"
                    color="textSecondary"
                  >
                    {t("PROよりお得な超プラン")}
                  </Typography>
                </CardContent>
                <PriceArea>
                  {hasCampaign && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      align="center"
                      display="block"
                      sx={{ textDecoration: "line-through" }}
                    >
                      {t("通常価格")} ¥{500 + (soundMasterPrice ?? 880)}/
                      {t("月")}
                    </Typography>
                  )}
                  <Typography variant="h4" align="center">
                    <Typography variant="caption">{t("月々")}</Typography>{" "}
                    {totalPrice}{" "}
                    <Typography variant="caption">{t("円")}</Typography>
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    align="center"
                    display="block"
                  >
                    ¥500 + ¥{effectivePrice}
                  </Typography>
                  {/* 一旦、ラベルの内容は無視する */}
                  {hasCampaign && campaignLabel && (
                    <Typography
                      variant="caption"
                      color="secondary"
                      align="center"
                      display="block"
                    >
                      {t("キャンペーン価格（初月のみ）")}
                    </Typography>
                  )}
                </PriceArea>
                <CardActions>
                  <ButtonLoading
                    variant="contained"
                    color="secondary"
                    onClick={onClickUpgradeWithSoundMaster}
                    disabled={isPro && isSoundMaster}
                  >
                    {isPro && isSoundMaster
                      ? t("加入済み")
                      : t("このプランにする")}
                  </ButtonLoading>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </FirstSection>
      <GlaySection>
        <Container maxWidth="md">
          <SubHeading>{t("プランの比較")}</SubHeading>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell size="small"></TableCell>
                  <TableCellPlanName>CCFOLIA BASIC</TableCellPlanName>
                  <TableCellPlanName>CCFOLIA PRO</TableCellPlanName>
                  <TableCellPlanName>
                    CCFOLIA PRO
                    <br />+ SOUND MASTER
                  </TableCellPlanName>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCellTh>{t("価格")}</TableCellTh>
                  <TableCell align="center">
                    <Typography variant="h5">{t("無料")}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="inline"
                    >
                      {t("月々")}
                    </Typography>
                    <br />
                    <Typography variant="h4" display="inline">
                      500
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="inline"
                    >
                      {t("円（税込）")}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="inline"
                    >
                      {t("月々")}
                    </Typography>
                    <br />
                    <Typography variant="h4" display="inline">
                      {totalPrice}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="inline"
                    >
                      {t("円（税込）")}
                    </Typography>
                    {/* 一旦、ラベルの内容は無視する */}
                    {hasCampaign && campaignLabel && (
                      <Typography
                        variant="caption"
                        color="secondary"
                        align="center"
                        display="block"
                      >
                        {t("キャンペーン価格（初月のみ）")}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCellTh>{t("基本機能")}</TableCellTh>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCellTh>
                    <Word>{t("サウンド")}</Word>
                    <Word>{t("ライブラリ")}</Word>
                  </TableCellTh>
                  <TableCell align="center">
                    <No />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCellTh>
                    <Word>{t("50万点以上の")}</Word>
                    <Word>{t("高品質音源")}</Word>
                  </TableCellTh>
                  <TableCell align="center">
                    <No />
                  </TableCell>
                  <TableCell align="center">
                    <No />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCellTh>
                    <Word>{t("音源設定の")}</Word>
                    <Word>{t("引き継ぎ")}</Word>
                  </TableCellTh>
                  <TableCell align="center">
                    <No />
                  </TableCell>
                  <TableCell align="center">
                    <No />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCellTh>{t("ルームデータ複製・出力")}</TableCellTh>
                  <TableCell align="center">
                    <No />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCellTh>{t("タイマー")}</TableCellTh>
                  <TableCell align="center">
                    <No />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCellTh>{t("告知枠表示設定")}</TableCellTh>
                  <TableCell align="center">
                    <No />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCellTh>
                    {t("スラッシュコマンド")}
                    <br />
                    <Typography variant="caption" color="textSecondary">
                      {t("(チャットでのルーム操作)")}
                    </Typography>
                  </TableCellTh>
                  <TableCell align="center">
                    <No />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCellTh>{t("チャットログ出力")}</TableCellTh>
                  <TableCell align="center">
                    <Typography variant="body1" display="inline">
                      {t("最大1万件")}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body1" display="inline">
                      {t("無制限")}
                    </Typography>
                    {/* <Trans>
                      <Typography variant="h5" display="inline">
                        100,000
                      </Typography>
                      <Typography variant="body1" display="inline">
                        件
                      </Typography>
                    </Trans> */}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body1" display="inline">
                      {t("無制限")}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCellTh>
                    {t("ターボモード")}{" "}
                    <Typography variant="caption" color="secondary">
                      NEW!
                    </Typography>
                    <br />
                    <Typography variant="caption" color="textSecondary">
                      {t("(ルーム読み込みの混雑回避)")}
                    </Typography>
                  </TableCellTh>
                  <TableCell align="center">
                    <No />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCellTh>
                    {t("画像の高画質アップロード")}{" "}
                    <Typography variant="caption" color="secondary">
                      NEW!
                    </Typography>
                  </TableCellTh>
                  <TableCell align="center">
                    <No />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                  <TableCell align="center">
                    <Yes />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </GlaySection>
      <Section>
        <SubHeading align="center">{t("よくある質問")}</SubHeading>
        <Container maxWidth="md">
          <ArticleQuestion>
            <Grid container wrap="nowrap" spacing={2}>
              <Grid item>
                <Typography variant="h6" color="textSecondary">
                  Q.
                </Typography>
              </Grid>
              <Grid item xs zeroMinWidth>
                <Typography color="textSecondary">
                  {t("サウンドライブラリとはどのようなものですか？")}
                </Typography>
              </Grid>
            </Grid>
            <Grid container wrap="nowrap" spacing={2}>
              <Grid item>
                <Typography variant="h6" color="secondary">
                  A.
                </Typography>
              </Grid>
              <Grid item xs zeroMinWidth>
                <Typography color="textPrimary">
                  {t(
                    "ツール内にあらかじめ用意されているセッションやセッション配信で自由に利用可能な音源素材です。音楽を使うことで、セッションをより盛り上げることができます。"
                  )}
                </Typography>
              </Grid>
            </Grid>
          </ArticleQuestion>
          <ArticleQuestion>
            <Grid container wrap="nowrap" spacing={2}>
              <Grid item>
                <Typography variant="h6" color="textSecondary">
                  Q.
                </Typography>
              </Grid>
              <Grid item xs zeroMinWidth>
                <Typography color="textSecondary">
                  {t("CCFOLIA PRO では告知枠が見えなくなってしまうのですか？")}
                </Typography>
              </Grid>
            </Grid>
            <Grid container wrap="nowrap" spacing={2}>
              <Grid item>
                <Typography variant="h6" color="secondary">
                  A.
                </Typography>
              </Grid>
              <Grid item xs zeroMinWidth>
                <Typography color="textPrimary">
                  {t(
                    "告知枠の表示設定はいつでも切り替えることができます。配信や録画などの関係で告知枠が表示されて欲しくない場合にオプションでお使いいただける機能です。"
                  )}
                </Typography>
              </Grid>
            </Grid>
          </ArticleQuestion>
          {/* <ArticleQuestion>
            <Grid container wrap="nowrap" spacing={2}>
              <Grid item>
                <Typography variant="h6" color="textSecondary">
                  Q.
                </Typography>
              </Grid>
              <Grid item xs zeroMinWidth>
                <Typography color="textSecondary">
                  クレジットカードを持っていないのですが、支払いはできますか？
                </Typography>
              </Grid>
            </Grid>
            <Grid container wrap="nowrap" spacing={2}>
              <Grid item>
                <Typography variant="h6" color="secondary">
                  A.
                </Typography>
              </Grid>
              <Grid item xs zeroMinWidth>
                <Typography color="textPrimary">
                  クレジットカードをお持ちでない場合は、Kyash等の電子ウォレットサービスをご利用いただくことができます。
                </Typography>
              </Grid>
            </Grid>
          </ArticleQuestion> */}
          <ArticleQuestion>
            <Grid container wrap="nowrap" spacing={2}>
              <Grid item>
                <Typography variant="h6" color="textSecondary">
                  Q.
                </Typography>
              </Grid>
              <Grid item xs zeroMinWidth>
                <Typography color="textSecondary">
                  {t("収益はどのように使われますか？")}
                </Typography>
              </Grid>
            </Grid>
            <Grid container wrap="nowrap" spacing={2}>
              <Grid item>
                <Typography variant="h6" color="secondary">
                  A.
                </Typography>
              </Grid>
              <Grid item xs zeroMinWidth>
                <Typography color="textPrimary">
                  {t(
                    "主にココフォリアのサーバーや開発・運営にかかる費用と、開発メンバーの給与に当てられます。"
                  )}
                </Typography>
              </Grid>
            </Grid>
          </ArticleQuestion>
          <ArticleQuestion>
            <Grid container wrap="nowrap" spacing={2}>
              <Grid item>
                <Typography variant="h6" color="textSecondary">
                  Q.
                </Typography>
              </Grid>
              <Grid item xs zeroMinWidth>
                <Typography color="textSecondary">
                  {t("今後、追加予定の機能はありますか？")}
                </Typography>
              </Grid>
            </Grid>
            <Grid container wrap="nowrap" spacing={2}>
              <Grid item>
                <Typography variant="h6" color="secondary">
                  A.
                </Typography>
              </Grid>
              <Grid item xs zeroMinWidth>
                <Typography color="textPrimary">
                  {t(
                    "よりセッションを盛り上げるための便利機能を追加していく予定です。お楽しみに！"
                  )}
                </Typography>
              </Grid>
            </Grid>
          </ArticleQuestion>
          <ArticleQuestion>
            <Grid container wrap="nowrap" spacing={2}>
              <Grid item>
                <Typography variant="h6" color="textSecondary">
                  Q.
                </Typography>
              </Grid>
              <Grid item xs zeroMinWidth>
                <Typography color="textSecondary">
                  {t(
                    "ルームに設定された SOUND MASTER 提供音源は、プラン解除後はどうなりますか？"
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid container wrap="nowrap" spacing={2}>
              <Grid item>
                <Typography variant="h6" color="secondary">
                  A.
                </Typography>
              </Grid>
              <Grid item xs zeroMinWidth>
                <Typography color="textPrimary">
                  {t(
                    "ルームやシーンへの設定情報は残りますが、音源は再生できなくなります。プランに再加入することで再び再生ができるようになります。"
                  )}
                </Typography>
              </Grid>
            </Grid>
          </ArticleQuestion>
        </Container>
      </Section>
      <StyledFooter>
        <Typography color="textSecondary" align="center">
          &copy; {year} ccfolia.com
        </Typography>
        <div style={{ textAlign: "center" }}>
          <Typography variant="caption" color="textSecondary">
            <Link href="/termsOfService.html" target="_blank" underline="hover">
              {t("利用規約")}
            </Link>
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ marginLeft: 8 }}
          >
            <Link
              href="/termsOfSoundLibrary.html"
              target="_blank"
              underline="hover"
            >
              {t("音源利用規約")}
            </Link>
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ marginLeft: 8 }}
          >
            <Link href="/termsForSales.html" target="_blank" underline="hover">
              {t("特定商取引法に基づく表示")}
            </Link>
          </Typography>
        </div>
      </StyledFooter>
    </StyledWrapper>
  );
};

type ButtonLoadingProps = {
  variant: "outlined" | "contained";
  onClick: () => Promise<void>;
  children: ReactNode;
  color?: "inherit" | "secondary";
  disabled?: boolean;
};

const ButtonLoading = ({
  variant,
  onClick,
  children,
  color,
  disabled,
}: ButtonLoadingProps) => {
  const [loading, setLoading] = useState(false);

  if (loading) {
    return (
      <Button variant="contained" fullWidth disabled>
        <CircularProgress color="primary" size={24.5} />
      </Button>
    );
  }

  const handleClick = () => {
    setLoading(true);
    onClick().finally(() => setLoading(false));
  };

  return (
    <Button
      variant={variant}
      color={color}
      fullWidth
      onClick={handleClick}
      disabled={disabled}
    >
      {children}
    </Button>
  );
};

type ButtonUpgradeProps = {
  isPro: boolean;
  onClick: () => Promise<void>;
};

const ButtonUpgrade = ({ isPro, onClick }: ButtonUpgradeProps) => {
  const [t] = useTranslation();

  return (
    <ButtonLoading
      variant="contained"
      color="inherit"
      onClick={onClick}
      disabled={isPro}
    >
      {isPro ? t("加入済み") : t("このプランにする")}
    </ButtonLoading>
  );
};

const Yes = () => <CheckIcon fontSize="large" color="secondary" />;
const No = () => <CloseIcon fontSize="large" color="disabled" />;

const StyledWrapper = styled.div`
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
  background-size: cover;
  position: relative;
`;

const FirstSection = styled(Container)`
  padding: 96px 0 56px;
`;

const GlaySection = styled(Paper)`
  padding: 56px 0;
`;

const Section = styled(Container)`
  padding: 56px 0;
`;

const StyledTitle = styled(Typography)`
  font-weight: bold;
  line-height: 1.4;
  margin-bottom: 24px;
`;

const StyledFooter = styled.div`
  margin-top: 32px;
  padding: 16px;
  position: relative;
  z-index: 1;
`;

const SubHeading = styled(Typography).attrs({
  variant: "h6",
  color: "textPrimary",
  align: "center",
})`
  margin: 0 0 24px 0;
`;

const PriceArea = styled(CardContent)`
  background: ${theme.palette.grey[900]};
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

const TableCellPlanName = styled(TableCell).attrs({
  align: "center",
  size: "small",
})`
  background: ${theme.palette.grey[900]};
  line-height: 1.4;
`;

const TableCellTh = styled(TableCell).attrs({
  component: "th",
  scope: "row",
  align: "center",
})`
  max-width: 80px;
  min-width: 40px;
  white-space: normal;
  background: #202020;
  color: ${theme.palette.text.secondary};
  font-size: 0.8rem;
`;

const ArticleQuestion = styled.div`
  margin: 24px 0;
`;

const Word = styled.span`
  white-space: nowrap;
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 48px;

  padding: 4px 0;

  svg {
    height: 16px;
  }
`;

const SoundMasterLogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 48px;
  margin-bottom: 8px;
`;

const CombinedLogoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  min-height: 48px;

  padding: 4px 0;

  svg:first-child {
    height: 16px;
  }

  svg:last-child {
    height: auto;
    max-width: 90px;
  }
`;

export default Welcome;
